import { MutationHookOptions, useMutation } from '@apollo/client'

import {
  UpdateOrderShippingDocument,
  UpdateOrderShippingInput,
  UpdateOrderShippingMutation,
  UpdateOrderShippingMutationVariables,
  UpdateShippingDetailDoorDocument,
} from '__generated__/atlas-types'

import { atlasClient } from 'graphql/client'
import { useRenderToasters } from 'hooks/useRenderToasters'

import { ShippingAddressFormValues } from './shippingAddressForm.types'
import { formatShippingFormValuesToShippingInfo } from './shippingAddressForm.utils'

export const useUpdateShippingAddress = (
  options?: MutationHookOptions<
    UpdateOrderShippingMutation,
    UpdateOrderShippingMutationVariables
  >,
) => {
  const [updateShippingAddressMutationFunction, { loading, error }] =
    useMutation<
      UpdateOrderShippingMutation,
      UpdateOrderShippingMutationVariables
    >(UpdateOrderShippingDocument, {
      ...options,
      client: atlasClient,
    })

  const updateShippingAddress = (
    orderId: string,
    formValues: ShippingAddressFormValues,
    overrideSourceAddress?: boolean,
  ) => {
    const input: UpdateOrderShippingInput = {
      orderId,
      shippingInfo: formatShippingFormValuesToShippingInfo(formValues),
      overrideSourceAddress,
    }

    return updateShippingAddressMutationFunction({
      variables: { input },
    })
  }

  return {
    updateShippingAddress,
    loading,
    error,
  }
}

export const useUpdateDoor = () => {
  const { renderErrorToast } = useRenderToasters()
  const [mutate, { loading }] = useMutation(UpdateShippingDetailDoorDocument, {
    client: atlasClient,
    onError: () => {
      renderErrorToast({
        title: 'Error',
        description: 'Error updating the door.',
      })
    },
  })

  const updateDoor = async (orderId: string, doorId: string) => {
    try {
      const response = await mutate({
        variables: {
          orderId,
          doorId,
        },
        notifyOnNetworkStatusChange: false,
      })

      if (!response.data?.updateOrderDeliveryInfo?.updatedOrderIds?.length) {
        throw new Error()
      }
    } catch (_err) {
      renderErrorToast({
        title: 'Error',
        description: 'Error updating the door.',
      })
    }
  }

  return { updateDoor, isUpdatingDoor: loading }
}
